import { alpha, createTheme } from '@mui/material/styles';

const defaultBrand = {
  primary: '#1f3d7c',
  secondary: '#2389ba',
  // Modernized default success/accent: a clean emerald reads far more clearly as
  // "success" than the previous olive, and harmonizes with the navy/blue brand.
  // Tenants can still override this via branding.accent_color.
  accent: '#1f9d6b',
};

const normalizeHex = (value, fallback) => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  const hex = text.startsWith('#') ? text : `#${text}`;
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
};

const clamp = (value, min = 0, max = 255) => Math.min(max, Math.max(min, value));

const adjustHexColor = (hex, amount = 0) => {
  const normalized = normalizeHex(hex, '#000000').slice(1);
  const channels = [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16));
  const next = channels.map((channel) => clamp(Math.round(channel + amount)));
  return `#${next.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const neutral = {
  0: '#ffffff',
  50: '#f8f9fc',
  100: '#f1f4f9',
  200: '#e3e8f2',
  300: '#cfd7e6',
  400: '#a9b5ca',
  500: '#7f8ea8',
  600: '#5d6a84',
  700: '#434e66',
  800: '#2a3348',
  900: '#1b2335',
  950: '#111826',
};

const darkNeutral = {
  0: '#0b111d',
  50: '#101828',
  100: '#131d30',
  200: '#1a2438',
  300: '#27344c',
  400: '#3f4d68',
  500: '#6a7892',
  600: '#8d9ab2',
  700: '#b1bcd2',
  800: '#d3d9e6',
  900: '#e8ecf5',
  950: '#f7f9fd',
};

const sharedShape = {
  borderRadius: 12,
};

const sharedTypography = {
  fontFamily: '"Manrope", "Sora", "Segoe UI", sans-serif',
  h1: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '2.25rem',
    lineHeight: 1.18,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '1.9rem',
    lineHeight: 1.2,
    fontWeight: 800,
    letterSpacing: '-0.018em',
  },
  h3: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '1.55rem',
    lineHeight: 1.25,
    fontWeight: 760,
    letterSpacing: '-0.015em',
  },
  h4: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '1.3rem',
    lineHeight: 1.3,
    fontWeight: 740,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '1.1rem',
    lineHeight: 1.34,
    fontWeight: 720,
    letterSpacing: '-0.01em',
  },
  h6: {
    fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
    fontSize: '0.98rem',
    lineHeight: 1.36,
    fontWeight: 700,
  },
  subtitle1: {
    fontSize: '0.98rem',
    lineHeight: 1.45,
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: '0.86rem',
    lineHeight: 1.4,
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  body1: {
    fontSize: '0.95rem',
    lineHeight: 1.58,
    fontWeight: 500,
  },
  body2: {
    fontSize: '0.87rem',
    lineHeight: 1.52,
    fontWeight: 500,
  },
  caption: {
    fontSize: '0.78rem',
    lineHeight: 1.45,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  button: {
    fontWeight: 700,
    letterSpacing: '0.01em',
    textTransform: 'none',
  },
};

const softShadowLight = [
  'none',
  '0 1px 2px rgba(15, 23, 42, 0.06)',
  '0 2px 6px rgba(15, 23, 42, 0.08)',
  '0 6px 14px rgba(15, 23, 42, 0.09)',
  '0 10px 22px rgba(15, 23, 42, 0.10)',
  '0 14px 28px rgba(15, 23, 42, 0.12)',
  '0 18px 36px rgba(15, 23, 42, 0.13)',
  '0 22px 44px rgba(15, 23, 42, 0.14)',
  '0 24px 52px rgba(15, 23, 42, 0.15)',
  '0 28px 62px rgba(15, 23, 42, 0.16)',
  '0 32px 70px rgba(15, 23, 42, 0.16)',
  '0 34px 74px rgba(15, 23, 42, 0.17)',
  '0 36px 78px rgba(15, 23, 42, 0.18)',
  '0 38px 84px rgba(15, 23, 42, 0.18)',
  '0 40px 88px rgba(15, 23, 42, 0.18)',
  '0 42px 92px rgba(15, 23, 42, 0.19)',
  '0 44px 96px rgba(15, 23, 42, 0.19)',
  '0 46px 100px rgba(15, 23, 42, 0.20)',
  '0 48px 106px rgba(15, 23, 42, 0.20)',
  '0 50px 110px rgba(15, 23, 42, 0.20)',
  '0 52px 114px rgba(15, 23, 42, 0.21)',
  '0 54px 118px rgba(15, 23, 42, 0.21)',
  '0 56px 122px rgba(15, 23, 42, 0.21)',
  '0 58px 126px rgba(15, 23, 42, 0.22)',
  '0 60px 130px rgba(15, 23, 42, 0.22)',
];

const softShadowDark = [
  'none',
  '0 1px 2px rgba(0, 0, 0, 0.22)',
  '0 2px 6px rgba(0, 0, 0, 0.24)',
  '0 6px 14px rgba(0, 0, 0, 0.26)',
  '0 10px 22px rgba(0, 0, 0, 0.28)',
  '0 14px 28px rgba(0, 0, 0, 0.31)',
  '0 18px 36px rgba(0, 0, 0, 0.33)',
  '0 22px 44px rgba(0, 0, 0, 0.35)',
  '0 24px 52px rgba(0, 0, 0, 0.36)',
  '0 28px 62px rgba(0, 0, 0, 0.37)',
  '0 32px 70px rgba(0, 0, 0, 0.38)',
  '0 34px 74px rgba(0, 0, 0, 0.39)',
  '0 36px 78px rgba(0, 0, 0, 0.40)',
  '0 38px 84px rgba(0, 0, 0, 0.41)',
  '0 40px 88px rgba(0, 0, 0, 0.42)',
  '0 42px 92px rgba(0, 0, 0, 0.43)',
  '0 44px 96px rgba(0, 0, 0, 0.44)',
  '0 46px 100px rgba(0, 0, 0, 0.45)',
  '0 48px 106px rgba(0, 0, 0, 0.46)',
  '0 50px 110px rgba(0, 0, 0, 0.47)',
  '0 52px 114px rgba(0, 0, 0, 0.48)',
  '0 54px 118px rgba(0, 0, 0, 0.48)',
  '0 56px 122px rgba(0, 0, 0, 0.49)',
  '0 58px 126px rgba(0, 0, 0, 0.50)',
  '0 60px 130px rgba(0, 0, 0, 0.50)',
];

const buildPalette = (mode, branding = {}) => {
  const dark = mode === 'dark';
  const n = dark ? darkNeutral : neutral;
  const brand = {
    primary: normalizeHex(branding.primary_color, defaultBrand.primary),
    secondary: normalizeHex(branding.secondary_color, defaultBrand.secondary),
    accent: normalizeHex(branding.accent_color, defaultBrand.accent),
  };

  return {
    mode,
    primary: {
      main: brand.primary,
      light: adjustHexColor(brand.primary, 28),
      dark: adjustHexColor(brand.primary, -30),
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.secondary,
      light: adjustHexColor(brand.secondary, 24),
      dark: adjustHexColor(brand.secondary, -24),
      contrastText: '#ffffff',
    },
    secondary2: {
      main: brand.secondary,
      light: adjustHexColor(brand.secondary, 24),
      dark: adjustHexColor(brand.secondary, -24),
      contrastText: '#ffffff',
    },
    success: {
      main: brand.accent,
      light: adjustHexColor(brand.accent, 20),
      dark: adjustHexColor(brand.accent, -24),
      contrastText: '#ffffff',
    },
    warning: {
      main: '#c7812f',
      light: '#d79a53',
      dark: '#9f6121',
      contrastText: '#ffffff',
    },
    error: {
      main: '#cf445f',
      light: '#de6b82',
      dark: '#a6344b',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2b7ec8',
      light: '#5da0d9',
      dark: '#1f5f98',
      contrastText: '#ffffff',
    },
    neutral: n,
    background: {
      default: dark ? '#0c1220' : '#f4f6fb',
      paper: dark ? '#121a2b' : '#ffffff',
    },
    text: {
      primary: dark ? '#e8edf7' : '#1d283a',
      secondary: dark ? '#afbbcf' : '#5e6b81',
      disabled: dark ? '#7e8ca4' : '#8f9ab0',
    },
    divider: dark ? alpha('#d3d9e6', 0.16) : alpha('#2a3348', 0.12),
    action: {
      active: dark ? '#d9e1ef' : '#4a566e',
      hover: dark ? alpha('#d3d9e6', 0.08) : alpha('#2a3348', 0.05),
      selected: dark ? alpha('#d3d9e6', 0.14) : alpha('#2a3348', 0.09),
      disabled: dark ? alpha('#d3d9e6', 0.35) : alpha('#2a3348', 0.3),
      disabledBackground: dark ? alpha('#d3d9e6', 0.12) : alpha('#2a3348', 0.08),
      focus: alpha(brand.secondary, 0.36),
    },
  };
};

const buildHrTokens = (mode, palette) => {
  const dark = mode === 'dark';

  return {
    surface: {
      app: palette.background.default,
      paper: palette.background.paper,
      raised: dark ? alpha('#ffffff', 0.075) : alpha('#ffffff', 0.86),
      muted: dark ? alpha('#ffffff', 0.045) : alpha(palette.primary.main, 0.035),
      glass: dark ? alpha('#111a2d', 0.82) : alpha('#ffffff', 0.78),
    },
    line: {
      soft: dark ? alpha('#d3d9e6', 0.12) : alpha('#1f3d7c', 0.1),
      strong: dark ? alpha('#d3d9e6', 0.22) : alpha('#1f3d7c', 0.2),
    },
    radius: {
      xs: '8px',
      sm: '10px',
      md: '14px',
      lg: '18px',
      xl: '24px',
      pill: '999px',
    },
    shadow: {
      soft: dark ? '0 12px 30px rgba(0,0,0,0.34)' : '0 14px 34px rgba(31,61,124,0.10)',
      lift: dark ? '0 18px 44px rgba(0,0,0,0.42)' : '0 22px 52px rgba(31,61,124,0.14)',
      glow: `0 18px 44px ${alpha(palette.success.main, dark ? 0.18 : 0.16)}`,
    },
    gradient: {
      hero: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 54%, ${alpha(
        palette.secondary.main,
        dark ? 0.78 : 0.72
      )} 100%)`,
      action: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.secondary.main} 100%)`,
      success: `linear-gradient(135deg, ${palette.success.main} 0%, ${palette.secondary.main} 100%)`,
      page: dark
        ? `radial-gradient(1100px 320px at 0% -8%, ${alpha(palette.secondary.main, 0.13)}, transparent 58%), radial-gradient(900px 300px at 100% -10%, ${alpha(palette.primary.main, 0.2)}, transparent 62%), linear-gradient(180deg, #0c1220 0%, #101728 100%)`
        : `radial-gradient(1100px 320px at 0% -8%, ${alpha(palette.secondary.main, 0.09)}, transparent 58%), radial-gradient(900px 300px at 100% -10%, ${alpha(palette.primary.main, 0.11)}, transparent 62%), linear-gradient(180deg, #f4f6fb 0%, #eef3fa 100%)`,
    },
    tone: {
      neutral: {
        bg: dark ? alpha('#ffffff', 0.07) : alpha(palette.primary.main, 0.06),
        fg: palette.text.secondary,
      },
      info: { bg: alpha(palette.info.main, dark ? 0.18 : 0.1), fg: palette.info.main },
      success: { bg: alpha(palette.success.main, dark ? 0.2 : 0.11), fg: palette.success.main },
      warning: { bg: alpha(palette.warning.main, dark ? 0.2 : 0.12), fg: palette.warning.main },
      danger: { bg: alpha(palette.error.main, dark ? 0.18 : 0.1), fg: palette.error.main },
      accent: { bg: alpha(palette.primary.main, dark ? 0.2 : 0.1), fg: palette.primary.main },
    },
  };
};

const componentOverrides = (theme) => {
  const dark = theme.palette.mode === 'dark';
  const focusRing = `0 0 0 3px ${alpha(theme.palette.secondary.main, dark ? 0.42 : 0.28)}`;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: theme.palette.mode,
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        body: {
          margin: 0,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          textRendering: 'optimizeLegibility',
          fontFeatureSettings: '"kern" 1',
        },
        '#root': {
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        },
        '*:focus-visible': {
          outline: 'none',
          boxShadow: focusRing,
          borderRadius: 8,
          transition: 'box-shadow 140ms ease',
        },
        '.rbc-calendar': {
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 12,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        },
        '.rbc-header': {
          fontWeight: 700,
          fontSize: '0.78rem',
          color: theme.palette.text.secondary,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.02) : theme.palette.neutral[100],
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '8px 4px',
        },
        '.rbc-month-view, .rbc-time-view, .rbc-agenda-view': {
          borderColor: theme.palette.divider,
        },
        '.rbc-day-bg + .rbc-day-bg, .rbc-header + .rbc-header': {
          borderLeftColor: theme.palette.divider,
        },
        '.rbc-off-range-bg': {
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.01) : theme.palette.neutral[50],
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: theme.hr?.radius?.md || 10,
          paddingInline: 16,
          minHeight: 40,
          fontWeight: 700,
          transition: theme.transitions.create(['background-color', 'transform', 'box-shadow', 'border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
        contained: {
          boxShadow: dark ? '0 8px 16px rgba(0, 0, 0, 0.34)' : '0 8px 16px rgba(31, 61, 124, 0.18)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: dark ? '0 12px 24px rgba(0, 0, 0, 0.4)' : '0 12px 24px rgba(31, 61, 124, 0.22)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, dark ? 0.2 : 0.08),
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        grouped: {
          minHeight: 38,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: theme.hr?.radius?.lg || 14,
          border: `1px solid ${theme.hr?.line?.soft || theme.palette.divider}`,
          boxShadow: theme.shadows[2],
          backgroundImage: 'none',
          transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
            duration: theme.transitions.duration.short,
          }),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
          backdropFilter: 'blur(8px)',
          backgroundColor: dark
            ? alpha(theme.palette.background.paper, 0.9)
            : alpha(theme.palette.background.paper, 0.86),
          color: theme.palette.text.primary,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundImage: dark
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.45)}, ${alpha(theme.palette.background.paper, 0.92)})`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)}, ${theme.palette.background.paper})`,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 42,
        },
        indicator: {
          height: 3,
          borderRadius: 999,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          borderRadius: theme.hr?.radius?.sm || 9,
          marginInline: 2,
          fontWeight: 700,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.primary.main, dark ? 0.24 : 0.1),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.white, 0.96),
          transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, dark ? 0.75 : 0.55),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 1.5,
          },
          '&.Mui-focused': {
            boxShadow: focusRing,
          },
          '&.Mui-disabled': {
            opacity: 0.72,
          },
        },
        input: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 18,
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, dark ? 0.2 : 0.1),
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: theme.palette.common.white,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
          letterSpacing: '0.01em',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionProps: {
          timeout: {
            enter: theme.transitions.duration.short,
            exit: theme.transitions.duration.shortest,
          },
        },
      },
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[5],
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          padding: '8px 10px',
          backgroundColor: dark ? alpha('#0e1422', 0.96) : alpha('#1b2335', 0.94),
          fontSize: '0.76rem',
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginInline: 6,
          marginBlock: 2,
          transition: theme.transitions.create(['background-color', 'transform'], {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            transform: 'translateX(2px)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.hr?.line?.soft || theme.palette.divider}`,
        },
        head: {
          fontWeight: 800,
          color: theme.palette.text.secondary,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.02) : theme.palette.neutral[100],
          fontSize: '0.78rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: theme.hr?.radius?.lg || 14,
          border: `1px solid ${theme.hr?.line?.soft || theme.palette.divider}`,
          boxShadow: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: dark
              ? alpha(theme.palette.secondary.main, 0.13)
              : alpha(theme.palette.secondary.main, 0.06),
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: theme.hr?.radius?.lg || 14,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.hr?.shadow?.lift || theme.shadows[5],
          backgroundImage: 'none',
        },
        option: {
          borderRadius: 10,
          margin: 4,
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        disableRowSelectionOnClick: true,
        rowHeight: 48,
        columnHeaderHeight: 46,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '--DataGrid-containerBackground': dark ? alpha(theme.palette.common.white, 0.02) : theme.palette.neutral[100],
        },
        columnHeaders: {
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.03) : theme.palette.neutral[100],
          fontWeight: 800,
        },
        row: {
          '&:nth-of-type(even)': {
            backgroundColor: dark ? alpha(theme.palette.common.white, 0.015) : alpha(theme.palette.primary.main, 0.015),
          },
          '&:hover': {
            backgroundColor: dark ? alpha(theme.palette.secondary.main, 0.11) : alpha(theme.palette.secondary.main, 0.08),
          },
        },
        columnHeader: {
          '&:focus-within': {
            outline: 'none',
            boxShadow: focusRing,
          },
        },
        cellCheckbox: {
          '&:focus-within': {
            outline: 'none',
            boxShadow: focusRing,
          },
        },
        cell: {
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:focus-within': {
            outline: 'none',
            boxShadow: focusRing,
          },
        },
        footerContainer: {
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.02) : theme.palette.neutral[50],
        },
      },
    },

    // ---- Expanded polish (whole-app restyle) --------------------------------
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: theme.spacing(2.5, 2.5, 1.5) },
        title: {
          fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
          fontWeight: 760,
          fontSize: '1.05rem',
          letterSpacing: '-0.01em',
        },
        subheader: { fontSize: '0.82rem', color: theme.palette.text.secondary, marginTop: 2 },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: theme.spacing(2.5), '&:last-child': { paddingBottom: theme.spacing(2.5) } },
      },
    },
    MuiCardActions: {
      styleOverrides: { root: { padding: theme.spacing(1.5, 2.5) } },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Sora", "Manrope", "Segoe UI", sans-serif',
          fontWeight: 760,
          fontSize: '1.15rem',
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { paddingTop: theme.spacing(1) } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: theme.spacing(2, 3) } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 8,
          backgroundColor: dark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.primary.main, 0.1),
        },
        bar: { borderRadius: 999 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.9rem',
          backgroundColor: alpha(theme.palette.primary.main, dark ? 0.3 : 0.14),
          color: theme.palette.primary.main,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.68rem', minWidth: 18, height: 18, padding: '0 5px' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: theme.hr?.radius?.md || 12,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.hr?.shadow?.lift || theme.shadows[6],
          marginTop: 6,
          backgroundImage: 'none',
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          margin: '1px 2px',
          minHeight: 38,
          fontSize: '0.88rem',
          fontWeight: 600,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, dark ? 0.2 : 0.08) },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, dark ? 0.28 : 0.12),
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, dark ? 0.32 : 0.16) },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 38, color: 'inherit' } },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: theme.hr?.radius?.sm || 10,
          textTransform: 'none',
          fontWeight: 700,
          borderColor: theme.palette.divider,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, dark ? 0.26 : 0.12),
            color: theme.palette.primary.main,
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, dark ? 0.3 : 0.16) },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: theme.hr?.radius?.md || 12,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          backgroundImage: 'none',
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 'auto' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { minHeight: 52, '&.Mui-expanded': { minHeight: 52 } },
        content: { fontWeight: 700 },
      },
    },
    MuiPaginationItem: {
      styleOverrides: { root: { borderRadius: 9, fontWeight: 700 } },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: theme.palette.text.disabled },
        li: { fontSize: '0.85rem', fontWeight: 600 },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: {
          fontWeight: 600,
          color: theme.palette.secondary.main,
          textDecorationColor: alpha(theme.palette.secondary.main, 0.4),
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 2, marginRight: 0, fontWeight: 500 } },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: '0.9rem', fontWeight: 500 } },
    },
    MuiInputAdornment: {
      styleOverrides: { root: { color: theme.palette.text.secondary } },
    },
    MuiSkeleton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: alpha('#0b111d', dark ? 0.7 : 0.5), backdropFilter: 'blur(2px)' },
        invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
      },
    },
    MuiFab: {
      styleOverrides: { root: { borderRadius: theme.hr?.radius?.lg || 16, textTransform: 'none', fontWeight: 700 } },
    },
    MuiRating: {
      styleOverrides: { iconFilled: { color: theme.palette.warning.main } },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: theme.hr?.radius?.md || 12,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.hr?.shadow?.lift || theme.shadows[6],
          backgroundImage: 'none',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600,
          '&.Mui-active': { fontWeight: 700 },
          '&.Mui-completed': { fontWeight: 700 },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active': { color: theme.palette.primary.main },
          '&.Mui-completed': { color: theme.palette.success.main },
        },
      },
    },
  };
};

export const createAppTheme = (mode = 'light', branding = {}) => {
  const palette = buildPalette(mode, branding);

  let theme = createTheme({
    palette,
    hr: buildHrTokens(mode, palette),
    spacing: 8,
    shape: sharedShape,
    typography: sharedTypography,
    shadows: mode === 'dark' ? softShadowDark : softShadowLight,
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 120,
        shorter: 160,
        short: 210,
        standard: 240,
        complex: 320,
        enteringScreen: 220,
        leavingScreen: 180,
      },
    },
    zIndex: {
      appBar: 1200,
      drawer: 1170,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
    customTokens: {
      radius: {
        xs: 8,
        sm: 10,
        md: 12,
        lg: 16,
        xl: 20,
      },
      spacingScale: {
        xs: 0.5,
        sm: 1,
        md: 1.5,
        lg: 2,
        xl: 3,
      },
      elevations: {
        low: mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.34)' : '0 6px 16px rgba(15,23,42,0.08)',
        medium: mode === 'dark' ? '0 12px 26px rgba(0,0,0,0.42)' : '0 12px 26px rgba(15,23,42,0.12)',
      },
      gradients: {
        hero: `linear-gradient(125deg, ${palette.primary.main}, ${palette.secondary.main})`,
        accent: `linear-gradient(125deg, ${palette.secondary.main}, ${palette.success.main})`,
      },
    },
  });

  theme = createTheme(theme, {
    components: componentOverrides(theme),
  });

  return theme;
};

export const themeBrand = defaultBrand;

